/** @format */

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { SearchIcon } from 'src/components/icons';
import { getContentItems } from 'src/slices/topics';
import { useDispatch, useSelector } from 'src/store';
import { buildSearchUrlSearch, SEARCH_URL_DEFAULTS } from 'src/utils/searchUrl';
import { formatDate } from 'date-fns';
import { formatExtranetLanguageCodeList, getExtranetLanguageDisplayCode, resolveExtranetSelectableLocale } from 'src/utils/extranetLanguages';
import { resolveSelectedLanguage } from 'src/utils/locale';

export function ExtranetTagList({ tags, topics }) {
  const navigate = useNavigate();
  const MAX_VISIBLE_TAGS = 3;
  const GAP_PX = 8;
  const containerRef = useRef(null);
  const measureRef = useRef(null);
  const widthCacheRef = useRef(new Map());
  const [visibleCount, setVisibleCount] = useState(Math.min(MAX_VISIBLE_TAGS, tags.length));

  const getTagWidth = useCallback(text => {
    const cachedWidth = widthCacheRef.current.get(text);
    if (cachedWidth) return cachedWidth;

    if (!measureRef.current) return 0;
    measureRef.current.textContent = text;
    const width = Math.ceil(measureRef.current.getBoundingClientRect().width);
    widthCacheRef.current.set(text, width);
    return width;
  }, []);

  const calculateVisibleCount = useCallback(() => {
    if (!tags.length) {
      setVisibleCount(0);
      return;
    }
    if (!containerRef.current) return;

    const containerWidth = containerRef.current.clientWidth;
    const maxTryCount = Math.min(MAX_VISIBLE_TAGS, tags.length);

    for (let count = maxTryCount; count >= 1; count -= 1) {
      const visibleTags = tags.slice(0, count);
      const hiddenCount = tags.length - count;

      let usedWidth = visibleTags.reduce((sum, tag) => sum + getTagWidth(tag), 0);
      usedWidth += GAP_PX * Math.max(visibleTags.length - 1, 0);

      if (hiddenCount > 0) {
        usedWidth += GAP_PX + getTagWidth(`+${hiddenCount}`);
      }

      if (usedWidth <= containerWidth) {
        setVisibleCount(count);
        return;
      }
    }

    setVisibleCount(1);
  }, [getTagWidth, tags]);

  useLayoutEffect(() => {
    widthCacheRef.current.clear();
    calculateVisibleCount();

    if (!containerRef.current || typeof ResizeObserver === 'undefined') return undefined;

    let rafId = 0;
    const observer = new ResizeObserver(() => {
      if (rafId) return;
      rafId = requestAnimationFrame(() => {
        rafId = 0;
        calculateVisibleCount();
      });
    });

    observer.observe(containerRef.current);
    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      observer.disconnect();
    };
  }, [calculateVisibleCount, tags]);

  const handleTopicClick = event => {
    event.stopPropagation();
    if (!topics[0]?.id) return;
    navigate(`/extranet/${encodeURIComponent(topics[0].id)}`);
  };

  const handleTagClick = (tag, event) => {
    event.stopPropagation();
    navigate(`/search${buildSearchUrlSearch({ ...SEARCH_URL_DEFAULTS, keywords: [tag] })}`);
  };

  return (
    <div className="extranet-widget--list-item-tags" ref={containerRef}>
      {topics[0] && (
        <span className="extranet-widget--list-item-topic" onClick={handleTopicClick}>
          {topics[0].name}
        </span>
      )}
      {tags.slice(0, visibleCount).map(tag => (
        <span className="extranet-widget--list-item-tag" key={tag} onClick={event => handleTagClick(tag, event)}>
          {tag}
        </span>
      ))}
      {tags.length > visibleCount && (
        <span className="extranet-widget--list-item-tag">+{tags.length - visibleCount}</span>
      )}
      <span className="extranet-widget--list-item-tag-measurer" ref={measureRef} aria-hidden="true" />
    </div>
  );
}

export function ExtranetItem({ item, locale }) {
  const navigate = useNavigate();
  const languageLabel =
    formatExtranetLanguageCodeList(item.languages) || getExtranetLanguageDisplayCode(item.language);

  const handleItemClick = () => {
    navigate(`/extranet/content/${item.id}/${locale}`);
  };

  return (
    <div
      className="extranet-widget--list-item"
      role="link"
      tabIndex={0}
      onClick={handleItemClick}
      onKeyDown={event => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          handleItemClick();
        }
      }}>
      <div className="extranet-widget--list-item-title-date">
        <div className="extranet-widget--list-item-title">{item.title}</div>
        <div className="extranet-widget--list-item-date">{formatDate(new Date(item.date), 'dd.MM.yyyy')}</div>
      </div>
      <div className="extranet-widget--list-item-languages">{languageLabel}</div>
      <div className="extranet-widget--list-item-text">{item.description}</div>
      <ExtranetTagList tags={item.tags} topics={item.topics} />
    </div>
  );
}

export function ExtranetWidget({ data }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { contentItems } = useSelector(state => state.topics);
  const dispatch = useDispatch();
  const [search, setSearch] = useState('');
  const contentLanguage = resolveExtranetSelectableLocale(resolveSelectedLanguage());

  const handleSubmit = event => {
    event.preventDefault();
    const keyword = search.trim();
    if (!keyword) return;
    navigate(`/search${buildSearchUrlSearch({ ...SEARCH_URL_DEFAULTS, keywords: [keyword] })}`);
  };

  useEffect(() => {
    dispatch(
      getContentItems({
        activeTopicBrand: 'all',
        selectedLanguage: contentLanguage,
        selectedSortBy: 'latest'
      })
    );
  }, [contentLanguage, dispatch]);

  return (
    <div className="extranet-widget" data-width={data.w} data-height={data.h}>
      <div className="extranet-widget--title">
        <h2>{data.title}</h2>
        <form onSubmit={handleSubmit} className="extranet-widget--search-form">
          <input
            type="text"
            name="q"
            placeholder={t('widget.searchExtranet')}
            value={search}
            onChange={event => setSearch(event.target.value)}
          />
          <button type="submit">
            <SearchIcon />
          </button>
        </form>
      </div>
      {contentItems && (
        <div className="extranet-widget--list">
          {contentItems.length > 0 ? (
            contentItems.map(d => <ExtranetItem item={d} key={d.id} locale={contentLanguage} />)
          ) : (
            <p className="extranet-widget--list-empty">{t('widget.noExtranetContent')}</p>
          )}
        </div>
      )}
      <div className="extranet-widget--link">
        <Link to={'/extranet'}>{t('widget.goToExtranetPortal')}</Link>
      </div>
    </div>
  );
}
